import type { FunctionComponent } from 'react'

import styles from './share-button.module.scss'
import { Button } from '@components/button/button'
import { Share } from '@components/icons/share'
import { snackbarStore } from '@lib/store/ui/snackbar'
import { TEXT } from '@lib/utils/text'
import { WEBVIEW_PREFIX } from '@shared/util/link'

const ShareButton: FunctionComponent<Props> = ({
    label = '',
    url = window.location.href,
    title = document.title,
    text = document.title,
    placement,
}) => {
    const handleShareClick = async () => {
        const sharePayload = {
            title,
            text,
            url,
        }

        // if url contains WEBVIEW_PREFIX, remove it
        if (url.includes(WEBVIEW_PREFIX)) {
            sharePayload.url = url.replace(WEBVIEW_PREFIX, '')
        }

        // sharing is not supported, fallback by copying link to clipboard instead
        if (
            typeof navigator?.canShare !== 'function' ||
            !(await navigator?.canShare(sharePayload))
        ) {
            // navigator.clipboard is not supported in non-secure contexts (localhost)
            if (!navigator?.clipboard) {
                const textarea = document.createElement('textarea')
                textarea.value = sharePayload.url
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)

                snackbarStore.show(TEXT.linkCopied, 'share.copied')
                return
            }

            try {
                await navigator.clipboard.writeText(sharePayload.url)

                snackbarStore.show(TEXT.linkCopied, 'share.copied')
                return
            } catch (error) {
                snackbarStore.show(TEXT.error, 'share.error')
            }
        }

        // sharing is supported
        try {
            await navigator.share(sharePayload)
        } catch (error) {
            // catch known exceptions and ignore them
            // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share#exceptions
            if (
                error instanceof Error &&
                [
                    'AbortError',
                    'NotAllowedError',
                    'TypeError',
                    'DataError',
                ].includes(error.name)
            ) {
                return
            }

            snackbarStore.show(TEXT.error, 'share.error')
        }
    }

    return (
        <Button
            type='button'
            className={styles['share-button']}
            analyticsProperties={{
                share: {
                    placement,
                    url,
                    text,
                    title,
                },
            }}
            size='medium'
            preset='anchor'
            onClick={handleShareClick}
        >
            <Share width='20' height='20' />
            {label}
        </Button>
    )
}

export default ShareButton

interface Props extends ShareProps {
    label?: string
}

export interface ShareProps {
    url?: string
    title: string
    text: string
    placement: string
}
